import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography, Paper, Button, Avatar, Alert } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import { useSearchParams } from "react-router-dom";

type InterviewIntakeResponseProps = {
  parsedContent: any;
};

type CandidateInformation = {
  name: string | null;
  candidate_id: string | null;
  email: string | null;
  experience: string | null;
  resume_link: string | null;
};

type RoleInformation = {
  role: string | null;
  job_family: string | null;
  job_description: string | null;
  job_description_type: "text" | "link" | null;
  language_preferance: string | null;
};

type IntakeData = {
  candidate_information: CandidateInformation;
  role_information: RoleInformation;
};

const getInitials = (name: string): string => {
  if (!name) return "??";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};


export default function InterviewIntakeResponse({
  parsedContent,
}: InterviewIntakeResponseProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [hasGenerated, setHasGenerated] = useState(false);

  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );

  const sessionId = useMemo(
    () => searchParams.get("sessionId"),
    [searchParams],
  );

  // Parse the intake data
  const intakeData: IntakeData | null = useMemo(() => {
    try {
      const finalAnswer = parsedContent?.finalAnswer;
      if (Array.isArray(finalAnswer) && finalAnswer.length > 0) {
        return JSON.parse(finalAnswer[0]);
      }
      return null;
    } catch (error) {
      console.error("Error parsing intake data:", error);
      return null;
    }
  }, [parsedContent]);

  const candidateName = useMemo(
    () => intakeData?.candidate_information?.name || "Candidate",
    [intakeData],
  );

  const candidateInitials = useMemo(
    () => getInitials(candidateName),
    [candidateName],
  );

  const candidateId = useMemo(
    () => intakeData?.candidate_information?.candidate_id || "N/A",
    [intakeData],
  );

  const handleGenerateInterviewPack = useCallback(async () => {
    if (!currentAgent || !intakeData || hasGenerated) return;

    setHasGenerated(true);

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload = {
      agentId: currentAgent.id,
      message: "generate interview pack",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    try {
      await dispatch(sendMessageToAgent(payload)).unwrap();
    } catch (error) {
      console.error("Error generating interview pack:", error);
    }
  }, [currentAgent, intakeData, sessionId, dispatch, hasGenerated]);

  if (!intakeData) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Unable to load candidate and role information. Please try again.
      </Alert>
    );
  }

  const { candidate_information, role_information } = intakeData;

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: "#004141", mb: 1, fontSize: "16px" }}
      >
        Review candidate and role context
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "#888", mb: 3, fontSize: "14px" }}
      >
        Please review the details below before generating the interview pack.
      </Typography>

      {/* Two Column Layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Candidate Information Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: "16px",
              color: "#004141",
            }}
          >
            Candidate Information
          </Typography>

          {/* Avatar and Name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                backgroundColor: "#e6f9f5",
                color: "#00a884",
                fontSize: "15px",
                fontWeight: 600,
              }}
            >
              {candidateInitials}
            </Avatar>
            <Box>
              <Typography
                sx={{ fontWeight: 600, fontSize: "15px", color: "#004141" }}
              >
                {candidateName}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#888" }}>
                {candidateId}
              </Typography>
            </Box>
          </Box>

          {/* Details */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {candidate_information.name && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Name
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                  {candidate_information.name}
                </Typography>
              </Box>
            )}

            {candidate_information.email && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Email
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                  {candidate_information.email}
                </Typography>
              </Box>
            )}

            {candidate_information.experience && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Experience
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                  {candidate_information.experience}
                </Typography>
              </Box>
            )}

            {candidate_information.resume_link && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Resume
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    window.open(candidate_information.resume_link!, "_blank")
                  }
                  sx={{
                    fontSize: "13px",
                    textTransform: "none",
                    borderColor: "#00d2aa",
                    color: "#004141",
                    padding: "4px 12px",
                    borderRadius: "6px",
                    "&:hover": {
                      borderColor: "#00b894",
                      backgroundColor: "#f8fffe",
                    },
                  }}
                >
                  View Resume
                </Button>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Role Information Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2.5,
              fontSize: "16px",
              color: "#004141",
            }}
          >
            Role Information
          </Typography>

          {/* Details */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {role_information.role && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Role
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#403f3f",
                    fontWeight: 500,
                    textAlign: "right",
                  }}
                >
                  {role_information.role}
                </Typography>
              </Box>
            )}

            {role_information.job_family && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Job Family
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                  {role_information.job_family}
                </Typography>
              </Box>
            )}

            {role_information.job_description && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Job Description
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    window.open(role_information.job_description!, "_blank")
                  }
                  sx={{
                    fontSize: "13px",
                    textTransform: "none",
                    borderColor: "#00d2aa",
                    color: "#004141",
                    padding: "4px 12px",
                    borderRadius: "6px",
                    "&:hover": {
                      borderColor: "#00b894",
                      backgroundColor: "#f8fffe",
                    },
                  }}
                >
                  View JD
                </Button>
              </Box>
            )}

            {role_information.language_preferance && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#888" }}>
                  Language Preference
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
                  {role_information.language_preferance
                    .charAt(0)
                    .toUpperCase() +
                    role_information.language_preferance.slice(1)}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Stage Validation Alert */}
      <Alert
        severity="success"
        icon={false}
        sx={{
          mb: 3,
          backgroundColor: "#f8fffe",
          border: "2px solid #00d2aa",
          borderRadius: 2,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#004141",
            mb: 0.5,
          }}
        >
          Stage Validation
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
          Candidate stage is "Hiring Manager Interview". You can proceed to
          generate the interview pack.
        </Typography>
      </Alert>

      {/* Generate Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleGenerateInterviewPack}
          disabled={hasGenerated}
          sx={{
            backgroundColor: "#00d2aa",
            color: "#ffffff",
            fontWeight: 500,
            fontSize: "14px",
            textTransform: "none",
            padding: "10px 24px",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#00b894",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#fff",
            },
          }}
        >
          Generate Interview Pack
        </Button>
      </Box>
    </Box>
  );
}
